<template>
    <div>
        <a @click="open" v-if="can.update.conference_room_booking">
            <slot>
                <button-primary>
                    <template slot="icon"><i class="fa fa-calendar-check-o"></i></template>
                    Konferenzraum buchen
                </button-primary>
            </slot>
        </a>
        <portal to="modal" v-if="showModal">
            <modal @close="showModal = false">

                <template slot="header">Buchung erstellen</template>

                <div class="mb-4">
                    <t-rich-select
                        :fetch-options="getUsers"
                        placeholder="Benutzer Suchen"
                        value-attribute="id"
                        text-attribute="full_name"
                        :minimum-input-length="1"
                        minimumInputLengthText="Bitte geben Sie ein oder mehrere Zeichen ein."
                        v-model="user"
                        searchBoxPlaceholder="Suchen..."
                        noResultsText="Keine Treffer"
                        searchingText="Suche..."
                    >
                    </t-rich-select>
                </div>
                <div class="grid grid-cols-12 gap-6 mb-16">

                    <date-time-range-select v-model="range"
                                            time-selection-mode="free"
                                            :has-repeating="true"
                    ></date-time-range-select>

                    <div class="mb-4 col-span-12">
                        <select-menu
                            class="mb-4 col-span-12 sm:col-span-6"
                            v-model="floor"
                            :values="[null,...floors.map(g => g.id)]"
                            :text="['Ohne Etage',...floors.map(g => g.name)]">
                            Etage
                        </select-menu>
                    </div>

                    <div class="mb-4 col-span-12">
                        <conference-room-navigation v-if="floor" v-model="selectedIds" :items="rooms"
                                                    :unavailable="unavailable"
                                                    @toggle="selectRoom"></conference-room-navigation>
                    </div>

                    <div class="mb-4 col-span-12" v-if="hasEventFields">
                        <conference-room-event-details-input :room="selectedRoom"
                                                             v-model="event"></conference-room-event-details-input>
                    </div>

                    <input-text v-model="comment" class="mb-4 col-span-12">
                        <span v-if="!comment.length && group.notice_field_conference_room_booking_is_required"
                              style="color: red">Notiz*</span>
                        <span v-else>Notiz</span>
                    </input-text>

                    <div v-if="selectedRoom"
                         class="mb-4 col-span-12">
                        <div class="text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2">Raum</div>
                        <div>
                            <a @click="selectedRoom = null"
                               class="cursor-pointer inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-office-primary text-white"
                            >
                                {{ selectedRoom.name }}
                                <button type="button"
                                        class="flex-shrink-0 ml-1.5 inline-flex text-white focus:outline-none focus:text-white"
                                        aria-label="">
                                    <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                        <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                    </svg>
                                </button>
                            </a>
                            <span class="text-gray-500 text-xs">
                                {{ selectedRoom.details }}
                            </span>
                        </div>

                    </div>
                </div>

                <div>
                    <div v-show="page === 'overview'">

                        <loading v-show="loading"></loading>

                        <tailwind-table v-show="rooms.length > 0 " class="mt-8"
                                        :header="['Raum', 'Details', 'Personen', '']">
                            <tr v-for="(room, i) in rooms" :class="{'bg-gray-50': i % 2}">
                                <td class="px-6 py-3">{{ room.name }}</td>
                                <td class="px-6 py-3">{{ room.details ?? '-' }}</td>
                                <td class="px-6 py-3"><i class="fa fa-users mr-2"></i>{{ room.persons }}</td>
                                <td class="px-6 py-3 text-right">
                                    <button-white @click="selectedRoom = room">Auswählen</button-white>
                                </td>
                            </tr>
                        </tailwind-table>

                        <p v-show="rooms.length === 0">Keine buchbaren Räume gefunden.</p>

                    </div>

                </div>

                <div v-if="page === 'confirm'">

                    <room-layout-select
                        class="mb-8"
                        :items="selectedRoom.room_layouts"
                        v-model="roomLayout"
                    ></room-layout-select>

                    <multi-select-small
                        class="mb-8"
                        v-show="selectedRoom.postable_equipment.length > 0"
                        :text="selectedRoom.postable_equipment.map(e => e.name)"
                        :values="selectedRoom.postable_equipment.map(e => e.id)"
                        :quantity="selectedRoom.postableEquipment.map(e => e.quantity)"
                        v-model="postableEquipment">Zubuchbares Equipment
                    </multi-select-small>

                    <catering-select
                        class="mb-8"
                        :items="selectedRoom.caterings"
                        v-model="caterings"
                    ></catering-select>

                    <p class="mb-8">
                        Soll der Konferenzraum
                        <span class="font-bold">{{ selectedRoom.name }}</span>
                        am <span class="font-bold">{{ date | date }}</span> gebucht werden?
                    </p>

                </div>
                <template slot="action">
                    <button-primary v-show="page === 'confirm'"
                                    :disabled="!!(group.notice_field_conference_room_booking_is_required && comment.length === 0)"
                                    @click="create">
                        <template slot="icon"><i class="fa fa-calendar-check-o"></i></template>
                        Konferenzraum buchen
                    </button-primary>
                </template>

            </modal>
        </portal>
    </div>
</template>

<script>
import Modal from "./Modal";
import ButtonSecondary from "./UI/ButtonSecondary";
import ButtonPrimary from "./UI/ButtonPrimary";
import InputText from "./UI/InputText";
import SelectMenu from "./UI/SelectMenu";
import TailwindTable from "./UI/TailwindTable";
import ButtonWhite from "./UI/ButtonWhite";
import Loading from "./UI/Loading";
import MultiSelect from "./UI/MultiSelect.vue";
import MultiSelectSmall from "./UI/MultiSelectSmall.vue";
import RoomLayoutSelect from "./RoomLayoutSelect.vue";
import CateringSelect from "./CateringSelect.vue";
import ConferenceRoomNavigation from "./ConferenceRoomNavigation.vue";
import TRichSelect from "vue-tailwind/dist/t-rich-select";
import DateTimeRangeSelect from "./DateTimeRangeSelect.vue";
import ConferenceRoomEventDetailsInput from "./ConferenceRoomEventDetailsInput.vue";

export default {
    name: "ConferenceRoomBookingCreate",
    components: {
        ConferenceRoomEventDetailsInput,
        DateTimeRangeSelect,
        TRichSelect,
        ConferenceRoomNavigation,
        CateringSelect,
        RoomLayoutSelect,
        MultiSelectSmall,
        MultiSelect,
        Loading,
        ButtonWhite,
        TailwindTable,
        SelectMenu,
        InputText,
        ButtonPrimary,
        ButtonSecondary,
        Modal,
    },
    inject: ['group', 'can'],

    data: () => ({
        floors: [],
        floor: null,
        showModal: false,
        loading: false,
        date: null,
        comment: '',
        rooms: [],
        selectedRoom: null,
        postableEquipment: [],
        roomLayout: null,
        caterings: [],

        unavailable: [],
        selectedIds: [],
        user: null,
        range: {
            start: (new Date()).toISOString().substring(0, 10) + ' 06:00',
            end: (new Date()).toISOString().substring(0, 10) + ' 07:00',
        },
        event: {
            title: '',
            description: '',
            supervisor: '',
            phone: '',
            person: 0,
        },
    }),

    filters: {
        date(val) {
            let [y, m, d] = val.split('-');
            return `${d}.${m}.${y}`;
        }
    },

    watch: {
        floor() {
            this.search()
        },
        range: {
            deep: true,
            handler() {
                this.selectedRoom = null;
                let [y, m, d] = this.range.start.substring(0, 10).split('-');
                this.date = `${y}-${m}-${d}`;
                this.search();
            },
        }
    },

    computed: {
        page() {
            if (this.selectedRoom) {
                return 'confirm';
            }

            return 'overview';
        },
        start() {
            return this.range.start;
        },
        end() {
            return this.range.end;
        },
        startTime() {
            return this.range.start.substring(11, 16)
        },
        endTime() {
            return this.range.end.substring(11, 16)
        },
        hasEventFields() {
            return this.group.has_conference_room_booking_events;
        }
    },

    methods: {
        async open(cr = null) {
          this.showModal = true;
          this.date = (new Date()).toISOString().slice(0, 10);

          // Load floors
          const response = await axios.get('/admin/floors');
          this.floors = await response.data.data;

            // Load rooms and wait for the request to complete
            await this.search();

            if (cr) {
              this.selectRoom(cr.id);
            }
        },
        search() {
            this.loading = true;
            this.postableEquipment = [];
            let params = {
                start: this.start,
                end: this.end
            }

            if (this.range.isSeries) {
                params.repeat = this.range.repeat;
                params.every = this.range.every;
                params.until = this.range.until;

                if (this.range.error) {
                    this.loading = false;
                    return;
                }
            }

            if (this.floor) {
                params["floor_id"] = this.floor;
            }
            this.roomLayout = null
          return window.axios.get(`/admin/conference_room_bookings`, {params})
                .then(response => {
                    this.loading = false;
                    this.rooms = response.data.data;
                    this.rooms = Object.keys(this.rooms).map((key) => this.rooms[key]);
                    this.unavailable = response.data.unavailable;
                    this.unavailable = Object.keys(this.unavailable).map((key) => this.unavailable[key]);
                })
                .catch(error => {
                    this.loading = false;
                    this.$root.$emit('error', error);
                });
        },
        create() {
            this.loading = true;
            let params = {
                start: this.start,
                end: this.end,
                comment: this.comment,
                conference_room_id: this.selectedRoom.id,
                postable_equipment: this.postableEquipment,
                room_layout: this.roomLayout,
                caterings: this.caterings,
                user_id: this.user,
            };

            if (this.hasEventFields) {
                params.event = this.event;
            }

            if (this.range.isSeries) {
                params.repeat = this.range.repeat;
                params.every = this.range.every;
                params.until = this.range.until;

                if (this.range.error) {
                    this.loading = false;
                    return;
                }
            }

            window.axios.post('/admin/conference_room_bookings', params).then((result) => {
                this.loading = false;
                this.showModal = false;

                if (result.data.data.mail_message_id) {
                    this.$root.$emit('email', result.data.data.mail_message_id);
                }

                this.$emit('created')
                this.$root.$emit('alert', 'Buchung erstellt.');
            }).catch(error => {
                this.loading = false;
                this.$root.$emit('error', error);
            })
        },
      selectRoom(id) {
        if (!id || (this.selectedRoom && this.selectedRoom.id === id)) {
          this.selectedIds = [];
          this.selectedRoom = null;
        }
        else {
          this.selectedIds = [id];
          this.selectedRoom = this.rooms.find(r => r.id === id);
        }
      },
        bookRoom(cr) {
            this.floor = cr.floor_id;
            this.open(cr);

        },
        getUsers(search) {
            return this.$http
                .get('/admin/users', {params: {search}})
                .then((data) => ({results: [{id: 'guest', full_name: "Gästebuchung"}, ...data.data.data]}))
                .catch(error => {
                    this.$root.$emit('error', error);
                });
        },
    }

}
</script>

<style scoped>

</style>